import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  VStack,
  HStack,
  IconButton,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { Formik, Form, FieldArray } from 'formik';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import * as Yup from 'yup';
import TextEditor from 'ui/TextEditor/TextEditor';

// Validation Schema
const validationSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  imageUrl: Yup.string().url('Invalid URL'),
  videoUrl: Yup.string().url('Invalid URL'),
  content: Yup.string(),
  category: Yup.string().required('Category is required'),
  isFeatured: Yup.boolean(),
  tags: Yup.array().of(Yup.string()),
});

const BlogDetailsFrom = ({ data, updateBlog, isEdit, isDisabled }) => {
  // console.log('initial values in bloginfoform: ', data);
  const [refresh, setRefresh] = useState(true);
  return (
    <Formik
      initialValues={data || {}}
      validationSchema={validationSchema}
      enableReinitialize
    // onSubmit={handleSubmit}
    >
      {({ values, handleChange, setFieldValue, errors, touched }) => (
        <Form>
          <VStack spacing={4} align="stretch">
            {/* Title */}
            <FormControl isRequired isInvalid={errors.title && touched.title}>
              <FormLabel>Title</FormLabel>
              <Input
                name="title"
                value={values.title}
                onChange={handleChange}
                placeholder="Enter title"
              />
            </FormControl>

            {/* Image URL */}
            <FormControl>
              <FormLabel>Image URL</FormLabel>
              <Input
                name="imageUrl"
                value={values.imageUrl}
                onChange={handleChange}
                placeholder="Enter image URL"
              />
            </FormControl>

            {/* Video URL */}
            <FormControl>
              <FormLabel>Video URL</FormLabel>
              <Input
                name="videoUrl"
                value={values.videoUrl}
                onChange={handleChange}
                placeholder="Enter video URL"
              />
            </FormControl>

            {/* Category and Tags on the Same Row */}
            <Flex gap={4}>
              <FormControl
                isRequired
                isInvalid={errors.category && touched.category}
                flex="40%"
              >
                <FormLabel>Category</FormLabel>
                <Select
                  name="category"
                  value={values.category}
                  onChange={handleChange}
                  placeholder="Select category"
                >
                  <option value="news">News</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="opinion">Opinion</option>
                  <option value="internal">Internal</option>
                </Select>
              </FormControl>

              <FormControl flex="60%">
                <FormLabel>Tags</FormLabel>
                <FieldArray
                  name="tags"
                  render={(arrayHelpers) => (
                    <Box>
                      {values.tags?.map((tag, index) => (
                        <HStack key={index} mb={1}>
                          <Input
                            name={`tags[${index}]`}
                            value={values.tags[index]}
                            onChange={handleChange}
                            placeholder={`Tag ${index + 1}`}
                          />
                          <IconButton
                            aria-label="Remove Tag"
                            icon={<DeleteIcon />}
                            onClick={() => arrayHelpers.remove(index)}
                            colorScheme="red"
                          />
                        </HStack>
                      ))}
                      <Button
                        leftIcon={<AddIcon />}
                        onClick={() => arrayHelpers.push('')}
                        colorScheme="green"
                        variant="outline"
                        size="sm"
                        mt={2}
                      >
                        Add Tag
                      </Button>
                    </Box>
                  )}
                />
              </FormControl>
            </Flex>

            {/* Is Featured */}
            <FormControl>
              <Checkbox
                name="isFeatured"
                isChecked={values.isFeatured}
                onChange={handleChange}
              >
                Is Featured
              </Checkbox>
            </FormControl>
            <Divider />
            <FormControl isInvalid={errors.content && touched.content}>
              {/* <FormLabel>Content</FormLabel> */}
              <TextEditor
                id="content"
                value={values.content}
                updateFieldValue={(value) => setFieldValue('content', value)}
                placeholder="Enter content here..."
                errorMessage={
                  errors.content && touched.content ? errors.content : ''
                }
                refresh={refresh}
                updateRefresh={setRefresh}
              />
            </FormControl>

            {/* Submit Button */}
            {isEdit ? (
              <Box textAlign="center" mt={6}>
                <Button
                  disabled={isDisabled}
                  variant="outline"
                  colorScheme="green"
                  size="md"
                  mr={4}
                  mb={8}
                  onClick={() => updateBlog(values, 1)}
                >
                  Save
                </Button>
                <Button
                  disabled={isDisabled}
                  colorScheme="green"
                  size="md"
                  ml={4}
                  mb={8}
                  onClick={() => updateBlog(values, 2)}
                >
                  Publish
                </Button>
              </Box>
            ) : (
              <Box textAlign="center" mt={4}>
                <Button
                  disabled={isDisabled}
                  colorScheme="green"
                  size="md"
                  mb={8}
                  onClick={() => updateBlog(values, 1)}
                >
                  Create
                </Button>
              </Box>
            )}
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

BlogDetailsFrom.propTypes = {
  updateBlog: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
    ])
  ),
};

export default BlogDetailsFrom;
